import { useCallback, useEffect, useState } from 'react';

import { reconvertEgsDrafts } from './egsModel';
import type { EgsDraft, EgsDraftInput, EgsDraftPayload } from './types';

function toDrafts(records: Array<EgsDraftInput>): Array<EgsDraft> {
  const now = new Date().toISOString();
  return records.map((record) => ({
    ...record,
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
  }));
}

export function useEgsDrafts(offerId: string | undefined) {
  const [drafts, setDrafts] = useState<Array<EgsDraft>>([]);

  useEffect(() => {
    setDrafts([]);
  }, [offerId]);

  const addRecords = useCallback((records: Array<EgsDraftInput>) => {
    const newDrafts = toDrafts(records);
    setDrafts((prevDrafts) => [...prevDrafts, ...newDrafts]);
    return newDrafts;
  }, []);

  // Drafts `keep` picks stay as they are, id included, since the id is the record's Salesforce ref.
  const replaceDrafts = useCallback(
    (records: Array<EgsDraftInput>, keep: (draft: EgsDraft) => boolean = () => false) => {
      setDrafts((prevDrafts) => [...prevDrafts.filter(keep), ...toDrafts(records)]);
    },
    [],
  );

  const removeDraft = useCallback((id: string) => {
    setDrafts((prevDrafts) => prevDrafts.filter((draft) => draft.id !== id));
  }, []);

  const loadDraft = useCallback((savedDrafts: Array<EgsDraftPayload>) => {
    const now = new Date().toISOString();
    setDrafts(savedDrafts.map((draft) => ({ ...draft, createdAt: now, updatedAt: now })));
  }, []);

  const reconvert = useCallback((currency: string, audToSourceCurrencyRate: Record<string, number | undefined>) => {
    setDrafts((prevDrafts) => reconvertEgsDrafts(prevDrafts, currency, audToSourceCurrencyRate));
  }, []);

  return { drafts, addRecords, replaceDrafts, removeDraft, loadDraft, reconvert };
}
